import { CheckboxWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import {
  Box,
  FormGroup,
  Button,
  CircularProgress,
  FormControlLabel,
  Typography
} from '@material-ui/core'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import SectionTitle from '../../components/SectionTitle'
import * as notificationGraphql from '../../graphql/notifications'
import { useCursorPage } from '../../helpers/useCursorPage'
import { PreferenceHistory } from './PreferenceHistory'
import {
  EmptyState,
  ErrorState,
  LoadMoreButton,
  idempotencyKey,
  RETAINED_RESULTS,
  t
} from './shared'
import useStyles from './styles/PreferencesPanel.styles'
import { NotificationPreferenceInput, NotificationPreferenceRow } from './types'

export function PreferencesPanel({ showHistory }: { showHistory: boolean }) {
  const classes = useStyles()
  const {
    items: preferences,
    loading,
    error,
    hasMore,
    loadMore,
    refetch
  } = useCursorPage<Record<string, any>, NotificationPreferenceRow>({
    query: notificationGraphql.NOTIFICATION_PREFERENCES_QUERY,
    maxRetained: RETAINED_RESULTS,
    getPage: (data) =>
      data && {
        items: data.notificationPreferences?.preferences ?? [],
        nextPageToken: data.notificationPreferences?.page?.nextPageToken
      },
    withItems: (data, items) => ({
      ...data,
      notificationPreferences: {
        ...data.notificationPreferences,
        preferences: items
      }
    })
  })
  const [update, { loading: updating }] = useMutation(
    notificationGraphql.UPDATE_NOTIFICATION_PREFERENCES_MUTATION
  )
  const {
    data: channelData,
    loading: channelsLoading,
    error: channelsError,
    refetch: refetchChannels
  } = useQuery(notificationGraphql.IAM_NOTIFICATION_CHANNELS_QUERY)
  const [updateChannels, { loading: updatingChannels }] = useMutation(
    notificationGraphql.UPDATE_IAM_NOTIFICATION_CHANNELS_MUTATION
  )
  const [channelDraft, setChannelDraft] = useState<{
    inAppEnabled: boolean
    emailEnabled: boolean
    smsEnabled: boolean
  } | null>(null)
  const [dirtyPreferences, setDirtyPreferences] = useState<
    Record<string, NotificationPreferenceInput>
  >({})
  const channels = channelDraft ?? channelData?.myNotificationChannels
  useEffect(() => {
    if (channelData?.myNotificationChannels) setChannelDraft(null)
  }, [channelData])
  if (loading) return <CircularProgress aria-label={t('Loading preferences')} />
  if (error)
    return (
      <ErrorState
        message="Unable to load notification preferences."
        onRetry={() => refetch()}
      />
    )
  return (
    <Box>
      <SectionTitle title={t('Preferences')} />
      <Typography variant="subtitle1" className={classes.subHeading}>
        {t('Delivery channels')}
      </Typography>
      {channelsLoading ? (
        <CircularProgress aria-label={t('Loading delivery channels')} />
      ) : channelsError ? (
        <ErrorState
          message="Unable to load delivery channels."
          onRetry={() => refetchChannels()}
        />
      ) : channels ? (
        <>
          <FormGroup row>
            {(
              [
                ['inAppEnabled', 'In-app'],
                ['emailEnabled', 'Email'],
                ['smsEnabled', 'SMS']
              ] as const
            ).map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <CheckboxWithLabel
                    checked={channels[key]}
                    noHelperText
                    onChange={(event) =>
                      setChannelDraft({
                        ...channels,
                        [key]: event.currentTarget.checked
                      })
                    }
                  />
                }
                label={t(label)}
              />
            ))}
          </FormGroup>
          <div className={classes.actionRow}>
            <Button
              variant="contained"
              color="primary"
              disabled={updatingChannels || channelDraft === null}
              onClick={async () => {
                try {
                  await updateChannels({ variables: { input: channelDraft } })
                  toast.success(t('Delivery channels saved.'))
                  setChannelDraft(null)
                  await refetchChannels()
                } catch {
                  toast.error(t('Unable to save delivery channels.'))
                }
              }}>
              {updatingChannels ? t('Saving…') : t('Save delivery channels')}
            </Button>
          </div>
        </>
      ) : null}
      {preferences.length === 0 ? (
        <EmptyState label="Preferences" />
      ) : (
        // Grouped by category, one row each, rather than one entry per
        // category+channel pair. The flat form repeated the category key once
        // per channel, so the same dotted identifier appeared up to three
        // times in a row and most of the screen was that repetition.
        Object.entries(
          preferences.reduce<Record<string, typeof preferences>>(
            (grouped, item) => ({
              ...grouped,
              [item.categoryKey]: [...(grouped[item.categoryKey] ?? []), item]
            }),
            {}
          )
        ).map(([categoryKey, items]) => (
          <div className={classes.categoryRow} key={categoryKey}>
            <Typography className={classes.categoryName}>
              {categoryKey}
            </Typography>
            <FormGroup row className={classes.channelGroup}>
              {items.map((item) => {
                const key = `${item.categoryKey}:${item.channel}`
                return (
                  <FormControlLabel
                    key={key}
                    control={
                      <CheckboxWithLabel
                        checked={Boolean(
                          dirtyPreferences[key]?.enabled ?? item.enabled
                        )}
                        disabled={!item.userMutable}
                        noHelperText
                        // The visible label is the channel alone, so without
                        // this the checkbox announces "EMAIL" with no clue
                        // which category it belongs to. The accessible name
                        // still CONTAINS the visible text, so WCAG 2.5.3
                        // (Label in Name) holds.
                        inputProps={{
                          'aria-label': `${item.categoryKey} ${item.channel}`
                        }}
                        onChange={(event) => {
                          setDirtyPreferences((current) => ({
                            ...current,
                            [key]: {
                              categoryKey: item.categoryKey,
                              channel: item.channel,
                              enabled: event.currentTarget.checked
                            }
                          }))
                        }}
                      />
                    }
                    // The channel alone: the category is already the row's
                    // label, so repeating it here is what produced
                    // "distribution.booking.created · EMAIL" three times over.
                    label={item.channel}
                  />
                )
              })}
            </FormGroup>
          </div>
        ))
      )}
      {(preferences.some((item) => item.userMutable) ||
        Object.keys(dirtyPreferences).length > 0) && (
        <div className={classes.actionRow}>
          <Button
            variant="contained"
            color="primary"
            disabled={updating || Object.keys(dirtyPreferences).length === 0}
            onClick={async () => {
              try {
                await update({
                  variables: {
                    preferences: Object.values(dirtyPreferences),
                    idempotencyKey: idempotencyKey()
                  }
                })
                toast.success(t('Notification preferences saved.'))
                setDirtyPreferences({})
                await refetch()
              } catch (actionError) {
                toast.error(t('Unable to save notification preferences.'))
              }
            }}>
            {updating ? t('Saving…') : t('Save preferences')}
          </Button>
        </div>
      )}
      {hasMore && <LoadMoreButton onLoadMore={async () => loadMore()} />}
      {showHistory && <PreferenceHistory />}
    </Box>
  )
}
